import { useMemo, useState } from 'react';
import { Trans } from 'react-i18next';

import type { FormatHookParams } from '/business/shared/components/pages/question/types';

import useEmissionSourceDropdown from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeEmissionSourceDropdown';
import useHowWasCollected from '/business/shared/components/pages/question/formats/staticSharedFields/useHowWasCollected';
import useMakeQuantity from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeQuantity';
import useMakeSupplierName from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeSupplierName';
import {
  QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
  QUESTION_BRANCH_GROUP_KEY_INTENSITY,
} from '/business/shared/components/pages/question/questionEnums';
import WidgetIcon from '/business/shared/images/icons/expander-icon.svg';
import Button from '/shared/components/widgets/button/Button';
import WidgetAccordion from '/shared/components/widgets/widget-accordion/WidgetAccordion';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledParagraph from '/shared/styled/components/StyledParagraph';

import useAbsoluteEmissions from '/business/shared/components/pages/question/formats/staticSharedFields/useAbsoluteEmissions';
import useMakeProductDescription from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeProductDescription';
import useMakeTotalSpend from '/business/shared/components/pages/question/formats/staticSharedFields/useMakeTotalSpend';
import {
  useGetEmissionSourceFields,
  useGetQuantityDropdownValues,
  useGetUnitDropdownValues,
} from '/business/shared/components/pages/question/useFieldUnits';

export default function useFormatSharedScope3GoodsSupplier({
  question,
  questionFormatName,
  branchType,
  label,
  emissionSourcePlaceholder,
}: FormatHookParams) {
  const emissionSourceDropdown = useEmissionSourceDropdown({
    valuesArray: useGetEmissionSourceFields({ questionFormatName, question }),
    emissionSourcePlaceholder,
    label,
    errMsg: 'Please select a spend category',
  });
  const supplierName = useMakeSupplierName({});
  const productDescription = useMakeProductDescription();
  const supplierIntensityData = useAbsoluteEmissions({
    propName: 'supplier_intensity_data',
    dropdownPropName: 'supplier_intensity_data_unit',
    label: 'Supplier intensity data:',
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(false),
    errMsg: 'Please enter supplier intensity data',
  });
  const absoluteEmissions = useAbsoluteEmissions({
    intensityOrAbsoluteDropdownValue: useGetUnitDropdownValues(false),
  });
  const quantity = useMakeQuantity({
    quantityDropdownValues: useGetQuantityDropdownValues({ questionFormatName, emissionSourcesArray: [] }),
    label: 'Units purchased',
    errMsg: 'Please enter the number of units purchased',
  });
  const totalSpend = useMakeTotalSpend();
  const howWasCollected = useHowWasCollected();

  const pageSchema = useMemo(() => {
    return [
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Supplier intensity data
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received directly from suppliers based on carbon intensity/unit (e.g., kg CO
                  <sub>2</sub>e per units of product).
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      {
        isGroup: true,
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        render: ({ children }: { children: React.ReactNode[] }) => {
          return (
            <WidgetAccordion items={children}>
              <div className="text">
                <StyledHeading as="h2" $type="h2" $semibold>
                  Absolute emissions
                </StyledHeading>
                <StyledParagraph $smallText>
                  Emissions data received from suppliers as a total sum (total kg or tonnes CO<sub>2</sub>e) in relation
                  to products or services your business used.
                </StyledParagraph>
              </div>
              <div className="icon">
                <img src={WidgetIcon} alt="Expander icon" />
              </div>
            </WidgetAccordion>
          );
        },
      },
      // supplier emissions branch
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...emissionSourceDropdown,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...productDescription,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...supplierIntensityData,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...totalSpend,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        ...howWasCollected,
      },
      // absolute emissions branch
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...emissionSourceDropdown,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...supplierName,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...productDescription,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...absoluteEmissions,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...quantity,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...totalSpend,
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        ...howWasCollected,
      },
      // inject the buttons
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_INTENSITY,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_INTENSITY}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_INTENSITY);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
      {
        groupKey: QUESTION_BRANCH_GROUP_KEY_ABSOLUTE,
        reactElement: (
          <>
            <Button
              $primary
              testId="save-new"
              disabled={false}
              data-groupkey={QUESTION_BRANCH_GROUP_KEY_ABSOLUTE}
              onClick={() => {
                setQuestionGroupForValidation(QUESTION_BRANCH_GROUP_KEY_ABSOLUTE);
                setSaveCallback('saveNew');
              }}
            >
              {/* @i18n: Add */}
              <Trans i18nKey="business:Buttons.add" />
            </Button>
          </>
        ),
      },
    ];
  }, []);

  const [saveCallback, setSaveCallback] = useState<null | 'saveNew'>(null);
  const [questionGroupForValidation, setQuestionGroupForValidation] = useState<null | string>(null);

  return {
    pageSchema,
    saveCallback,
    questionGroupForValidation,
  };
}
